// frontend/src/components/Sidebar/GearPopup.js
import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { UserContext } from '../../UserContext';
import apiClient from '../../apiClient';
import { getPreferences, updatePreferences } from '../JournalEditor/api/journalApi';
import { ProductLabel } from '../common/MedicalDeviceMarks';
import './GearPopup.css';

function GearPopup({ onClose, onOpenPhraseManager }) {
  // Hämta user från UserContext
  const { user } = useContext(UserContext);

  // Läs journalsystem direkt från user.clinic
  const journalSystem = user?.clinic?.journal_system || 'Kaddio';
  const hasClinic = !!user?.clinic;
  const useInternalJournal = user?.clinic?.use_internal_journal || false;

  // Tab state
  const [activeTab, setActiveTab] = useState('system');

  // Certificate state (för SLS_PDF)
  const [hasCertificate, setHasCertificate] = useState(false);
  const [certLoading, setCertLoading] = useState(false);
  const [certError, setCertError] = useState(null);
  const [certSuccess, setCertSuccess] = useState(null);
  const [certPassword, setCertPassword] = useState('');
  const fileInputRef = useRef(null);

  // Journal preferences state (field names match backend)
  const [preferences, setPreferences] = useState({
    default_template_id: null,
    autosave_interval_seconds: 30,
    confirm_before_sign: true,
    show_quick_phrases: true,
    keyboard_shortcuts_enabled: true
  });
  const [templates, setTemplates] = useState([]);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaveStatus, setPrefsSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  const saveTimeoutRef = useRef(null);

  // Kolla om certifikat finns vid mount (endast för SLS_PDF)
  useEffect(() => {
    if (journalSystem === 'SLS_PDF') {
      apiClient.get('/api/certificate/status')
        .then(res => setHasCertificate(res.data.has_certificate))
        .catch(() => setHasCertificate(false));
    }
  }, [journalSystem]);

  // Load journal preferences and templates (only if internal journal is enabled)
  useEffect(() => {
    if (useInternalJournal && activeTab === 'journal') {
      loadPreferences();
      loadTemplates();
    }
  }, [useInternalJournal, activeTab]);

  const loadPreferences = async () => {
    try {
      setPrefsLoading(true);
      const data = await getPreferences();
      setPreferences(data.preferences || {});
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setPrefsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      // Läser från EHR:ets mallcache — fungerar även när mallkällan
      // (Notes efter uppdelningen) är onåbar
      const response = await apiClient.get('/api/journal/templates');
      setTemplates(response.data?.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  // Debounced save preferences
  const savePreferences = useCallback(async (newPrefs) => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setPrefsSaveStatus('saving');

    // Debounce the save
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await updatePreferences(newPrefs);
        setPrefsSaveStatus('saved');
        // Clear saved status after 2 seconds
        setTimeout(() => setPrefsSaveStatus(null), 2000);
      } catch (error) {
        console.error('Error saving preferences:', error);
        setPrefsSaveStatus('error');
      }
    }, 500);
  }, []);

  const handlePreferenceChange = (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    savePreferences(newPrefs);
  };

  // Hantera certifikat-uppladdning
  const handleCertificateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!certPassword.trim()) {
      setCertError('Ange lösenordet för certifikatet');
      return;
    }

    setCertLoading(true);
    setCertError(null);
    setCertSuccess(null);

    const formData = new FormData();
    formData.append('certificate', file);
    formData.append('password', certPassword);

    try {
      const response = await apiClient.post('/api/certificate/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setHasCertificate(true);
      setCertSuccess(`Certifikat uppladdat: ${response.data.certificate_info?.name || 'OK'}`);
      setCertPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.details || error.response?.data?.error || 'Kunde inte ladda upp certifikat';
      setCertError(errorMsg);
    } finally {
      setCertLoading(false);
      // Rensa fil-input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="gear-popup with-tabs">
      <div className="gear-popup-header">
        <h3>Inställningar</h3>
        <button onClick={onClose} className="close-popup-button">
          ✖
        </button>
      </div>

      {/* Tabs - "Om appen" visas alltid; journalflikarna endast för interna journalkliniker */}
      <div className="gear-popup-tabs">
        <button
          className={`gear-popup-tab ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          Journalsystem
        </button>
        {useInternalJournal && (
          <button
            className={`gear-popup-tab ${activeTab === 'journal' ? 'active' : ''}`}
            onClick={() => setActiveTab('journal')}
          >
            Journalinställningar
          </button>
        )}
        <button
          className={`gear-popup-tab ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          Om appen
        </button>
      </div>

      <div className="gear-popup-body">
        {/* Journal System Tab */}
        {activeTab === 'system' && (
          <>
            {useInternalJournal && (
              <div className="internal-journal-info">
                <p>
                  <i className="fas fa-check-circle" style={{ color: '#28a745', marginRight: '8px' }}></i>
                  Din klinik använder det <strong>inbyggda journalsystemet</strong>.
                </p>
                <p className="internal-journal-hint">
                  {journalSystem
                    ? `Journaler sparas och signeras direkt i systemet. Överföring till ${journalSystem} är också tillgänglig.`
                    : 'Journaler sparas och signeras direkt i systemet. Ingen extern överföring behövs.'
                  }
                </p>
              </div>
            )}
            {journalSystem && (
              <>
                {!useInternalJournal && (
                  <p>
                    Din enhet (klinik) använder: <strong>{journalSystem}</strong>
                  </p>
                )}

                {journalSystem === 'Kaddio' && (
                  <div className="kaddio-extension-info">
                    <p>Ladda ned överföringsverktyget (Chrome Extension) för Kaddio:</p>
                    <a
                      href="https://chromewebstore.google.com/detail/aion-notes-transfer-exten/hikcpghkdgniminoebfhpjemgfmcfpoi"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="kaddio-download-link"
                    >
                      Ladda ned extension
                    </a>
                  </div>
                )}

                {journalSystem === 'Clinicbuddy' && (
                  <div className="clinicbuddy-extension-info">
                    <p>Ladda ned (eller använd) överföringsverktyget för Clinicbuddy:</p>
                    <a
                      href="https://chromewebstore.google.com/detail/aion-notes-transfer-exten/hikcpghkdgniminoebfhpjemgfmcfpoi"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="clinicbuddy-download-link"
                    >
                      Ladda ned extension
                    </a>
                  </div>
                )}

                {journalSystem === "Webdoc" && (
                  <div className="webdoc-extension-info">
                    <p>Ladda ned överföringsverktyget för Webdoc:</p>
                    <a
                      href="https://chromewebstore.google.com/detail/aion-notes-transfer-exten/hikcpghkdgniminoebfhpjemgfmcfpoi"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ladda ned extension
                    </a>
                  </div>
                )}

                {journalSystem === 'PMO' && (
                  <div>
                    <p>Ingen särskild nedladdning tillgänglig för PMO i dagsläget.</p>
                  </div>
                )}

                {journalSystem === 'TakeCare' && (
                  <div>
                    <p>Klicka här för att ladda ned överföringsverktyget:</p>
                    <a
                      href="https://drive.google.com/file/d/1BoACvohyAuEEvH9TfA-0P8snrcpMYWNW/view?usp=drive_link"
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      Ladda ned TakeCare-verktyg
                    </a>
                  </div>
                )}

                {journalSystem === 'SLS_PDF' && (
                  <div className="sls-pdf-settings">
                    <h4>Digital signering</h4>
                    <p>
                      För att signera PDF:er digitalt behöver du ladda upp ditt
                      certifikat (.pfx-fil).
                    </p>

                    {hasCertificate ? (
                      <div className="cert-status cert-status-ok">
                        <span className="cert-icon">✓</span>
                        Certifikat uppladdat
                      </div>
                    ) : (
                      <div className="cert-status cert-status-missing">
                        <span className="cert-icon">!</span>
                        Inget certifikat uppladdat
                      </div>
                    )}

                    <div className="cert-upload-section">
                      <p className="cert-upload-label">
                        {hasCertificate ? 'Byt certifikat:' : 'Ladda upp certifikat:'}
                      </p>

                      <div className="cert-password-input">
                        <input
                          type="password"
                          placeholder="Certifikatets lösenord"
                          value={certPassword}
                          onChange={(e) => setCertPassword(e.target.value)}
                          disabled={certLoading}
                        />
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pfx,.p12"
                        onChange={handleCertificateUpload}
                        disabled={certLoading || !certPassword.trim()}
                        className="cert-file-input"
                      />

                      {certLoading && <p className="cert-loading">Laddar upp...</p>}
                      {certError && <p className="cert-error">{certError}</p>}
                      {certSuccess && <p className="cert-success">{certSuccess}</p>}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Journal Preferences Tab */}
        {activeTab === 'journal' && (
          <div className="journal-settings-section">
            <h4>
              <i className="fas fa-sliders-h"></i>
              Mina journalinställningar
            </h4>

            {prefsLoading ? (
              <p>Laddar inställningar...</p>
            ) : (
              <>
                {/* Default template */}
                <div className="journal-setting-group">
                  <label>Standardmall för nya journaler</label>
                  <select
                    value={preferences.default_template_id || ''}
                    onChange={(e) => handlePreferenceChange(
                      'default_template_id',
                      e.target.value ? parseInt(e.target.value) : null
                    )}
                  >
                    <option value="">-- Ingen standardmall --</option>
                    {templates.map((template) => (
                      <option key={template.template_id} value={template.template_id}>
                        {template.template_name}
                      </option>
                    ))}
                  </select>
                  <p className="journal-setting-hint">
                    Denna mall väljs automatiskt när du skapar en ny journal.
                  </p>
                </div>

                {/* Autosave interval */}
                <div className="journal-setting-group">
                  <label>Autosparningsintervall (sekunder)</label>
                  <select
                    value={preferences.autosave_interval_seconds || 30}
                    onChange={(e) => handlePreferenceChange(
                      'autosave_interval_seconds',
                      parseInt(e.target.value)
                    )}
                  >
                    <option value="10">10 sekunder</option>
                    <option value="15">15 sekunder</option>
                    <option value="30">30 sekunder</option>
                    <option value="60">1 minut</option>
                    <option value="120">2 minuter</option>
                    <option value="300">5 minuter</option>
                  </select>
                </div>

                {/* Toggle settings */}
                <div className="journal-toggle-group">
                  <span className="journal-toggle-label">Bekräfta före signering</span>
                  <label className="journal-toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.confirm_before_sign !== false}
                      onChange={(e) => handlePreferenceChange(
                        'confirm_before_sign',
                        e.target.checked
                      )}
                    />
                    <span className="journal-toggle-slider"></span>
                  </label>
                </div>

                <div className="journal-toggle-group">
                  <span className="journal-toggle-label">Tangentbordsgenvägar (Ctrl+S, etc)</span>
                  <label className="journal-toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.keyboard_shortcuts_enabled !== false}
                      onChange={(e) => handlePreferenceChange(
                        'keyboard_shortcuts_enabled',
                        e.target.checked
                      )}
                    />
                    <span className="journal-toggle-slider"></span>
                  </label>
                </div>

                {/* Quick phrases section */}
                {onOpenPhraseManager && (
                  <div className="quick-phrases-section">
                    <p className="journal-setting-hint" style={{ marginBottom: '10px' }}>
                      Skriv <kbd>;</kbd> i en journal följt av kortkommandot för att infoga en fras.
                    </p>
                    <button
                      className="manage-phrases-btn"
                      onClick={onOpenPhraseManager}
                    >
                      <i className="fas fa-bolt"></i>
                      Hantera snabbfraser
                    </button>
                  </div>
                )}

                {/* Save status indicator */}
                {prefsSaveStatus && (
                  <div className={`prefs-save-status ${prefsSaveStatus}`}>
                    {prefsSaveStatus === 'saving' && (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Sparar...
                      </>
                    )}
                    {prefsSaveStatus === 'saved' && (
                      <>
                        <i className="fas fa-check"></i>
                        Inställningar sparade
                      </>
                    )}
                    {prefsSaveStatus === 'error' && (
                      <>
                        <i className="fas fa-exclamation-triangle"></i>
                        Kunde inte spara
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* About / Produktinformation Tab (medicinteknisk etikett, MDR 2017/745) */}
        {activeTab === 'about' && (
          <div className="about-section">
            <h4>
              <i className="fas fa-info-circle"></i>
              Produktinformation
            </h4>
            <ProductLabel />
            <p className="about-ifu-hint">
              Fullständig bruksanvisning finns under <strong>Guide</strong> i menyn.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default GearPopup;
